import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { Can } from '@iq/react-components';
import ExportTimeSeriesModal from '../SiteAdminView/VariableView/components/ExportTimeSeriesModal';
import FullscreenModal from './visualizations/FullscreenModal';
import { getSiteVariables, getAggregatedConnectivity } from '../../bundles/sources';

import GraphVisualization, {
  schema as graphSchema,
  uiSchema as graphUiSchema,
} from './visualizations/GraphVisualization';
import MetricVisualization, {
  schema as metricSchema,
  uiSchema as metricUiSchema,
} from './visualizations/MetricVisualization';
import StatusVisualization, {
  schema as statusSchema,
  uiSchema as statusUiSchema,
} from './visualizations/StatusVisualization';
import BarGaugeVisualization, {
  schema as barGaugeSchema,
  uiSchema as barGaugeUiSchema,
} from './visualizations/BarGaugeVisualization';
import GaugeVisualization, {
  schema as gaugeSchema,
  uiSchema as gaugeUiSchema,
} from './visualizations/GaugeVisualization';
import StateVisualization, {
  schema as stateSchema,
  uiSchema as stateUiSchema,
} from './visualizations/StateVisualization';
import PieVisualization, {
  schema as pieSchema,
  uiSchema as pieUiSchema,
} from './visualizations/PieVisualization';
import StarVisualization, {
  schema as starSchema,
  uiSchema as starUiSchema,
} from './visualizations/StarVisualization';
import DuvalVisualization, {
  schema as duvalSchema,
  uiSchema as duvalUiSchema,
} from './visualizations/DuvalVisualization';
import DuvalPentagonVisualization, {
  schema as duvalPentagonSchema,
  uiSchema as duvalPentagonUiSchema,
} from './visualizations/DuvalPentagonVisualization';
import Tooltip from '../Tooltip/Tooltip';

const visualizations = [
  {
    name: 'Graph',
    type: 'graph',
    category: 'graphs',
    icon: 'he-line-chart-alt',
    component: GraphVisualization,
    schema: graphSchema,
    uiSchema: graphUiSchema,
  },
  {
    name: 'Metric',
    type: 'metric',
    category: 'metrics',
    icon: 'he-numericals',
    component: MetricVisualization,
    schema: metricSchema,
    uiSchema: metricUiSchema,
  },
  {
    name: 'Status',
    type: 'status',
    category: 'metrics',
    icon: 'he-status',
    component: StatusVisualization,
    schema: statusSchema,
    uiSchema: statusUiSchema,
  },
  {
    name: 'Bar gauge',
    type: 'bar-gauge',
    category: 'graphs',
    icon: 'he-speedometer',
    component: BarGaugeVisualization,
    schema: barGaugeSchema,
    uiSchema: barGaugeUiSchema,
  },
  {
    name: 'Gauge',
    type: 'gauge',
    category: 'graphs',
    icon: 'he-speedometer',
    component: GaugeVisualization,
    schema: gaugeSchema,
    uiSchema: gaugeUiSchema,
  },
  {
    name: 'State',
    type: 'state',
    category: 'State',
    icon: 'he-states',
    component: StateVisualization,
    schema: stateSchema,
    uiSchema: stateUiSchema,
  },
  {
    name: 'Pie',
    type: 'pie',
    category: 'Pie',
    icon: 'he-piechart',
    component: PieVisualization,
    schema: pieSchema,
    uiSchema: pieUiSchema,
  },
  {
    name: 'Star',
    type: 'star',
    category: 'Star',
    icon: 'he-favoritefill',
    component: StarVisualization,
    schema: starSchema,
    uiSchema: starUiSchema,
  },
  {
    name: 'Duval',
    subname: 'Triangle',
    type: 'duval',
    category: 'duval',
    icon: 'he-triangle',
    component: DuvalVisualization,
    schema: duvalSchema,
    uiSchema: duvalUiSchema,
  },
  {
    name: 'Duval',
    subname: 'Pentagon',
    type: 'duvalPentagon',
    category: 'duval',
    icon: 'he-pentagon',
    component: DuvalPentagonVisualization,
    schema: duvalPentagonSchema,
    uiSchema: duvalPentagonUiSchema,
  },
];

const config = (type) =>
  visualizations.find(({ type: _type }) => _type === type) || {
    schema: {},
    component: () => 'No component',
  };

const schema = (type) => config(type).schema;

const uiSchema = (type) => config(type).uiSchema;

const component = (type) => config(type).component;

const Visualization = ({ visualization, exportGranularity, ...props }) => {
  const siteVariables = useSelector(getSiteVariables);
  const aggregatedConnectivity = useSelector(getAggregatedConnectivity);

  const variables = visualization.variables.map((v) => {
    const variable = siteVariables.find(({ id }) => id === v.id);
    return {
      ...v,
      name: variable?.name,
      source: variable?.source,
      itemDesignation: variable?.itemDesignation,
    };
  });

  let disconnected = false;
  variables.forEach(({ source }) => {
    if (source && aggregatedConnectivity[source.name] === 0) {
      disconnected = true;
    }
  });

  const Component = component(visualization.type);
  const corevisualizationRef = useRef();
  const [showExportForm, setShowExportForm] = useState(false);

  const [showFullscreen, setShowFullscreen] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState({ x: 'right', y: 'bottom' });

  const handleCloseExportModal = useCallback(() => setShowExportForm(false), [setShowExportForm]);

  const handleExportData = useCallback(() => {
    setShowExportForm(true);
  }, []);

  const handleFullscreen = useCallback(() => {
    setShowFullscreen(true);
  }, []);

  const handleCloseFullscreenModal = useCallback(() => {
    setShowFullscreen(false);
  }, []);

  // Since the duval and star point don't display all signals,
  // we need to extract the signals that are actually assigned to plots
  const exportVariables = () => {
    switch (visualization.type) {
      case 'duval': {
        return visualization.configuration.plots
          .flatMap((plot) => [plot.leftId, plot.rightId, plot.bottomId])
          .map((signalId) => variables.find((v) => v.id === signalId?.split('.')[0]));
      }
      case 'duvalPentagon': {
        return visualization.configuration.plots
          .flatMap((plot) => [plot.h2Id, plot.c2h6Id, plot.ch4Id, plot.c2h4Id, plot.c2h2Id])
          .map((signalId) => variables.find((v) => v.id === signalId?.split('.')[0]));
      }
      case 'star': {
        const { L1, L2, L3 } = visualization.configuration.plot;
        return [L1, L2, L3]
          .map((signal) => variables.find((v) => v.id === signal?.split('.')[0]))
          .filter((variable) => variable);
      }
      default:
        return variables;
    }
  };

  const getMoreOptions = ({
    loading = false,
    site = {},
    isPreview = false,
    onFullscreen,
    data = [],
    handleDownloadClick,
    openSignalViewer,
  }) => {
    const options = [];
    const isDataAvailable = !loading && !isPreview && data?.length > 0;

    if (isDataAvailable && handleDownloadClick) {
      options.push({
        component: (
          <Can
            permission="variables/Write"
            scope={{ org: site.org, site: site.id }}
          >
            <div onClick={handleDownloadClick}>Download</div>
          </Can>
        ),
      });
    }

    if (openSignalViewer) {
      options.push({
        component: <div onClick={() => openSignalViewer(visualization)}>Open in Signal Viewer</div>,
      });
    }

    if (isDataAvailable && onFullscreen) {
      options.push({ component: <div onClick={onFullscreen}>Fullscreen</div> });
    }

    return options;
  };

  const updateMenuPlacement = useCallback(() => {
    if (corevisualizationRef.current) {
      const panelElement = corevisualizationRef.current?.parentElement?.parentElement;
      if (panelElement) {
        const { right: panelElementRight } = panelElement.getBoundingClientRect();
        const childRect = corevisualizationRef?.current.getBoundingClientRect();
        const spaceRight = panelElementRight - childRect.right;
        setDropdownPlacement({ x: spaceRight > 150 ? 'right' : 'left', y: 'bottom' });
      }
    }
  }, [corevisualizationRef]);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(updateMenuPlacement);
    if (corevisualizationRef.current) {
      resizeObserver.observe(corevisualizationRef.current);
    }

    return () => {
      if (corevisualizationRef.current) {
        resizeObserver.unobserve(corevisualizationRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [corevisualizationRef, updateMenuPlacement]);

  return (
    <>
      <div
        className="corevisualization-component"
        ref={corevisualizationRef}
      >
        <Component
          visualization={{ ...visualization, variables }}
          onExportData={handleExportData}
          onFullscreen={handleFullscreen}
          CustomTooltip={Tooltip}
          getMoreOptions={getMoreOptions}
          dropdownPlacement={dropdownPlacement}
          disconnected={disconnected}
          {...props}
        />
      </div>
      {showExportForm && visualization.variables?.length > 0 && (
        <ExportTimeSeriesModal
          selectedVizVariables={exportVariables()}
          pollingDateRange={props.pollingDateRange}
          onCloseModal={handleCloseExportModal}
          showAggregation={false}
          exportGranularity={exportGranularity}
          onExportCompleted={handleCloseExportModal}
          parentPanelId={props.panelId}
        />
      )}
      {showFullscreen && (
        <FullscreenModal
          visualization={visualization}
          handleExportData={handleExportData}
          onSelect={props.onSelect}
          openSignalViewer={props.openSignalViewer}
          onClose={handleCloseFullscreenModal}
          Component={
            <Component
              visualization={{ ...visualization, variables }}
              onExportData={handleExportData}
              onFullscreen={handleFullscreen}
              isFullscreen={showFullscreen}
              disconnected={disconnected}
              {...props}
            />
          }
        />
      )}
    </>
  );
};

export default Visualization;
export { schema, uiSchema, config, component, visualizations };
